import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { can, workingDepartmentKey } from "./access";
import { apiErrorMessage } from "./apiError";
import { timerRemaining } from "./ProductionPadi";

const TimerContext = createContext({ timers: [], refresh: async () => {}, openTray: () => {}, createTimer: async () => {}, unlockAudio: () => {}, playAlert: async () => {} });
export const useProductionTimers = () => useContext(TimerContext);

// A view of the existing server-backed operational timers, mounted above navigation.
export function ProductionTimerProvider({ api, user, children }) {
  const [timers, setTimers] = useState([]), [open, setOpen] = useState(false), [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const permitted = can(user, "recipes.view"), writable = can(user, "recipes.record_production");
  const alive = useRef(false), request = useRef(0), mutating = useRef(false);
  const audio = useRef(null), nextAlertAt = useRef(0), alertNotes = useRef([]);
  const unlockAudio = useCallback(() => {
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) return;
      audio.current ||= new Context();
      Promise.resolve(audio.current.resume?.()).catch(() => {});
      // Starting a silent note during the user's gesture unlocks Web Audio in Safari/Chrome.
      const oscillator = audio.current.createOscillator(), gain = audio.current.createGain();
      gain.gain.value = 0.0001;
      oscillator.connect(gain).connect(audio.current.destination);
      oscillator.start(); oscillator.stop(audio.current.currentTime + 0.01);
    } catch { /* The visible expiry state remains available. */ }
  }, []);
  const playAlert = useCallback(async () => {
    const context = audio.current;
    if (!context) return;
    try {
      alertNotes.current.forEach(note => { try { note.disconnect(); } catch {} });
      alertNotes.current = [];
      if (context.state !== "running") await context.resume?.();
      if (context.state !== "running") return;
      const start = Math.max(context.currentTime, nextAlertAt.current);
      for (let note = 0; note < 3; note++) {
        const oscillator = context.createOscillator(), gain = context.createGain();
        oscillator.type = "sine"; oscillator.frequency.value = note % 2 ? 660 : 880;
        gain.gain.setValueAtTime(0.0001, start + note * 0.25);
        gain.gain.exponentialRampToValueAtTime(0.28, start + note * 0.25 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + note * 0.25 + 0.2);
        oscillator.connect(gain).connect(context.destination);
        oscillator.start(start + note * 0.25); oscillator.stop(start + note * 0.25 + 0.21);
        alertNotes.current.push(oscillator);
      }
      nextAlertAt.current = start + 0.75;
    } catch { /* Browser audio policy may block sound; visual alert persists. */ }
  }, []);
  const silenceAlert = useCallback(() => {
    alertNotes.current.forEach(note => { try { note.stop(); } catch {} try { note.disconnect(); } catch {} });
    alertNotes.current = [];
    nextAlertAt.current = 0;
  }, []);
  useEffect(() => () => { silenceAlert(); try { Promise.resolve(audio.current?.close?.()).catch(() => {}); } catch {} }, [silenceAlert]);
  const refresh = useCallback(async () => {
    if (!permitted) return;
    const sequence = ++request.current;
    try {
      const selected = window.sessionStorage.getItem(workingDepartmentKey(user));
      const departmentId = selected && (user.all_departments || user.role === "Owner/Admin" || (user.department_ids || []).includes(selected)) ? selected : "";
      const response = await api.get(`/production-timers${departmentId ? `?department_id=${encodeURIComponent(departmentId)}` : ""}`);
      if (alive.current && sequence === request.current) { setTimers(response.data); setError(""); }
    } catch (e) {
      if (alive.current && sequence === request.current) setError(apiErrorMessage(e, "Timers could not refresh"));
    }
  }, [api, permitted, user]);
  useEffect(() => {
    alive.current = true; refresh();
    const id = setInterval(refresh, 15000);
    const visible = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", refresh); document.addEventListener("visibilitychange", visible);
    return () => { alive.current = false; ++request.current; clearInterval(id); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", visible); };
  }, [refresh]);
  const update = async (timer, action, minutes) => {
    if (mutating.current) return;
    mutating.current = true; setBusy(true);
    try { await api.post(`/production-timers/${timer.id}/${action}`, action === "done" ? {} : { minutes }); if (action === "done") silenceAlert(); await refresh(); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not update timer")); }
    finally { mutating.current = false; setBusy(false); }
  };
  const createTimer = async (label, minutes) => {
    if (!(Number(minutes) > 0) || !label.trim()) return;
    const selected = window.sessionStorage.getItem(workingDepartmentKey(user));
    const department_id = selected && (user.all_departments || user.role === "Owner/Admin" || (user.department_ids || []).includes(selected)) ? selected : (user.department_ids || [])[0] || null;
    await api.post("/production-timers", { label: label.trim(), minutes: Number(minutes), department_id });
    await refresh();
  };
  return <TimerContext.Provider value={{ timers, refresh, open, setOpen, openTray: () => setOpen(true), createTimer, unlockAudio, playAlert, silenceAlert, error, permitted, writable, busy, update }}>{children}</TimerContext.Provider>;
}

export function GlobalTimerCompanion() {
  const { timers, open, setOpen, error, permitted, writable, busy, update, refresh, createTimer, unlockAudio, playAlert, silenceAlert } = useProductionTimers();
  const [clock, setClock] = useState(Date.now());
  const [label, setLabel] = useState(""), [minutes, setMinutes] = useState(5);
  const [creating, setCreating] = useState(false);
  const durationInput = useRef(null);
  const alerted = useRef(new Set());
  const [edge, setEdge] = useState(() => { try { return window.localStorage.getItem("murai-timer-edge") === "left" ? "left" : "right"; } catch { return "right"; } });
  useEffect(() => { const id = setInterval(() => setClock(Date.now()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    const newlyReached = timers.filter(timer => new Date(timer.due_at).getTime() <= clock && !alerted.current.has(timer.id));
    if (!newlyReached.length) return;
    newlyReached.forEach(timer => alerted.current.add(timer.id));
    setOpen(true);
    toast.warning(`${newlyReached[0].label || "Kitchen timer"} · Time reached`);
  }, [timers, clock, playAlert, setOpen]);
  const expiredIds = timers.filter(timer => new Date(timer.due_at).getTime() <= clock).map(timer => timer.id).sort().join(":");
  useEffect(() => {
    if (!expiredIds) { silenceAlert(); return undefined; }
    playAlert();
    const interval = window.setInterval(playAlert, 1500);
    return () => window.clearInterval(interval);
  }, [expiredIds, playAlert, silenceAlert]);
  if (!permitted) return null;
  const ordered = [...timers].sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
  const urgent = ordered[0], reached = urgent && new Date(urgent.due_at).getTime() <= clock;
  const move = () => { const next = edge === "right" ? "left" : "right"; setEdge(next); try { window.localStorage.setItem("murai-timer-edge", next); } catch {} };
  return <aside className={`global-timer-companion edge-${edge}`} aria-label="Kitchen timer companion">
    {open && <section className="global-timer-tray" aria-label="Active kitchen timers">
      <header><strong>Timers <small>{timers.length}</small></strong><div className="row-actions"><button className="text-btn timer-add-button" type="button" disabled={!writable} title={writable ? "Add timer" : "Requires production recording access"} onClick={() => setCreating(value => !value)} aria-expanded={creating}>+ Add timer</button><button className="text-btn" type="button" aria-label="Move timer drawer" title="Move timer drawer" onClick={move}>↔</button><button className="text-btn" type="button" aria-label="Close timers" onClick={() => setOpen(false)}>×</button></div></header>
      {writable && creating && <form className="global-timer-create" onSubmit={async event => { event.preventDefault(); unlockAudio(); try { await createTimer(label || "Kitchen timer", minutes); setLabel(""); setCreating(false); } catch (e) { toast.error(apiErrorMessage(e, "Could not start timer")); } }}><label>Timer name<input aria-label="Timer name" placeholder="e.g. Proofing" value={label} onChange={event => setLabel(event.target.value)}/></label><div className="global-timer-presets">{[5, 10, 30].map(value => <button key={value} type="button" className={Number(minutes) === value ? "selected" : ""} onClick={() => setMinutes(value)}>{value} min</button>)}<button type="button" onClick={() => durationInput.current?.focus()}>Custom</button></div><label>Duration (minutes)<input ref={durationInput} aria-label="Timer minutes" type="number" min="0.01" step="any" value={minutes} onChange={event => setMinutes(event.target.value)}/></label><div className="row-actions"><button type="button" className="text-btn" onClick={() => setCreating(false)}>Cancel</button><button className="primary-btn" disabled={!(Number(minutes) > 0)}>Start timer</button></div></form>}
      {!writable && <small className="timer-permission-note">Timer creation requires production recording access.</small>}
      {error && <p role="status">{error} <button onClick={refresh}>Retry</button></p>}
      {!timers.length && <p>No active timers.</p>}
      {ordered.map(timer => <article key={timer.id} className="global-timer-row">
        <strong>{timer.label || timer.recipe_name || "Timer"}</strong>
        <output>{timerRemaining(timer.due_at, clock)}</output>
        <small>Started by {timer.started_by_staff || timer.created_by}</small>
        <div className="row-actions">{[5, 10].map(minutes => <button disabled={busy || !writable} key={minutes} onClick={() => update(timer, "extend", minutes)}>+{minutes}</button>)}
          <button disabled={busy || !writable} onClick={() => update(timer, "done")}>Done</button></div>
      </article>)}
    </section>}
    <button className={`timer-companion-button ${reached ? "timer-reached" : ""}`} aria-label={`Open kitchen timers, ${timers.length} active${reached ? ", time reached" : ""}`} aria-expanded={open} onClick={() => { unlockAudio(); setOpen(!open); }}>
      ⏱ {urgent ? timerRemaining(urgent.due_at, clock).replace(" remaining", "") : new Date(clock).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{timers.length > 1 && <small> · {timers.length}</small>}{error && " · !"}
    </button>
  </aside>;
}
